import React, { useState, useEffect, useRef } from 'react';
import { 
  LifeBuoy, 
  Send, 
  DollarSign, 
  User, 
  CheckCircle,
  X
} from 'lucide-react';

export default function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [modificationRequest, setModificationRequest] = useState(null);
  const [modRequestLoading, setModRequestLoading] = useState(false);
  const [driverDetails, setDriverDetails] = useState(null);
  const [comparisonDocUrl, setComparisonDocUrl] = useState(null);
  const [comparisonDocLoading, setComparisonDocLoading] = useState(false);
  const [comparisonDocIsPdf, setComparisonDocIsPdf] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('OPEN'); // OPEN, RESOLVED, ALL
  const [typeFilter, setTypeFilter] = useState('ALL'); // ALL, PAYMENT_ISSUE, DRIVER_MODIFICATION, etc.

  // Refund Modal State
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  // Modification Request Modal State
  const [modificationModalOpen, setModificationModalOpen] = useState(false);
  const [modificationAction, setModificationAction] = useState(''); // 'approve' or 'reject'
  const [modificationNote, setModificationNote] = useState('');

  const messagesEndRef = useRef(null);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/support/tickets', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des tickets');
      }

      const data = await response.json();
      setTickets(data);
      if (data.length > 0 && !selectedTicket) {
        handleSelectTicket(data[0].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = async (ticketId) => {
    setMessagesLoading(true);
    setModificationRequest(null);
    setDriverDetails(null);
    if (comparisonDocUrl) {
      URL.revokeObjectURL(comparisonDocUrl);
      setComparisonDocUrl(null);
    }
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/support/tickets/${ticketId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Impossible de charger les messages du ticket');
      }

      const data = await response.json();
      setSelectedTicket(data.ticket);
      setMessages(data.messages);
      
      // Update selected ticket status in local list directly without refetching
      setTickets(prev => prev.map(t => t.id === ticketId ? data.ticket : t));

      if (data.ticket && data.ticket.category === 'DRIVER_MODIFICATION' && data.ticket.modificationRequestId) {
        fetchModificationRequestDetails(data.ticket.modificationRequestId);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setMessagesLoading(false);
    }
  };

  const fetchModificationRequestDetails = async (requestId) => {
    setModRequestLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/driver-modifications/${requestId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const modData = await response.json();
        setModificationRequest(modData);
        if (modData && modData.driver) {
          fetchDriverDetails(modData.driver.id);
        }
      }
    } catch (err) {
      console.error('Error fetching modification request details:', err);
    } finally {
      setModRequestLoading(false);
    }
  };

  const fetchDriverDetails = async (driverId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/drivers/${driverId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const drData = await response.json();
        setDriverDetails(drData);
      }
    } catch (err) {
      console.error('Error fetching driver details:', err);
    }
  };

  const loadComparisonDocBlob = async (url) => {
    setComparisonDocLoading(true);
    try {
      const fileName = url.split('/').pop();
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/files/${fileName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setComparisonDocUrl(blobUrl);
      }
    } catch (err) {
      console.error('Error loading comparison document:', err);
    } finally {
      setComparisonDocLoading(false);
    }
  };

  useEffect(() => {
    if (comparisonDocUrl) {
      URL.revokeObjectURL(comparisonDocUrl);
      setComparisonDocUrl(null);
    }

    if (!driverDetails || !modificationRequest) return;

    const docs = driverDetails.documents || [];
    let targetDoc = null;

    if (modificationRequest.type === 'PROFILE_PHOTO') {
      targetDoc = docs.find(d => d.type === 'DRIVERS_LICENSE_RECTO') || 
                  docs.find(d => d.type === 'VTC_CARD_RECTO') ||
                  docs.find(d => d.type === 'DRIVERS_LICENSE_VERSO') ||
                  docs.find(d => d.type === 'VTC_CARD_VERSO');
    } else if (modificationRequest.type === 'VEHICLE_PHOTO' || modificationRequest.type === 'VEHICLE_INFO') {
      targetDoc = docs.find(d => d.type === 'CARTE_GRISE');
    }

    if (targetDoc && targetDoc.url) {
      setComparisonDocIsPdf(targetDoc.url.toLowerCase().endsWith('.pdf'));
      loadComparisonDocBlob(targetDoc.url);
    }

    return () => {
      if (comparisonDocUrl) {
        URL.revokeObjectURL(comparisonDocUrl);
      }
    };
  }, [driverDetails, modificationRequest?.id]);

  useEffect(() => {
    fetchTickets();
  }, []);

  // Scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;

    const textToSend = replyText;
    setReplyText('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/support/tickets/${selectedTicket.id}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: textToSend })
      });

      if (!response.ok) {
        throw new Error('Erreur d\'envoi de la réponse');
      }

      // Reload ticket messages
      await handleSelectTicket(selectedTicket.id);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/support/tickets/${ticketId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Erreur lors du changement de statut');
      }

      await handleSelectTicket(ticketId);
      await fetchTickets();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRefundSubmit = async (e) => {
    e.preventDefault();
    if (!refundAmount || !refundReason.trim() || !selectedTicket) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/support/tickets/${selectedTicket.id}/refund`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(refundAmount),
          reason: refundReason
        })
      });

      if (!response.ok) {
        throw new Error('Erreur d\'exécution du remboursement');
      }

      setRefundModalOpen(false);
      setRefundAmount('');
      setRefundReason('');
      
      // Reload ticket messages & status
      await handleSelectTicket(selectedTicket.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleModificationAction = async (action) => {
    if (!selectedTicket || !selectedTicket.modificationRequestId) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const endpoint = action === 'approve' ? 'approve' : 'reject';
      
      const response = await fetch(`/api/admin/driver-modifications/${selectedTicket.modificationRequestId}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note: modificationNote })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors du traitement');
      }

      setModificationModalOpen(false);
      setModificationNote('');
      setModificationAction('');
      
      // Reload ticket
      await handleSelectTicket(selectedTicket.id);
      await fetchTickets();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div style={styles.loadingContainer}>Chargement du support client...</div>;
  }

  const getCategoryLabel = (cat) => {
    switch (cat) {
      case 'PAYMENT_ISSUE': return 'Problème de Paiement';
      case 'DRIVER_BEHAVIOR': return 'Comportement Chauffeur';
      case 'CLIENT_BEHAVIOR': return 'Comportement Client';
      case 'APP_BUG': return 'Bug Application';
      case 'DOCUMENT_ISSUE': return 'Problème de Document';
      case 'DRIVER_MODIFICATION': return 'Demande de Modification';
      default: return 'Autre réclamation';
    }
  };

  // Filter tickets based on status and type
  const filteredTickets = tickets.filter(ticket => {
    // Status filter
    if (statusFilter === 'OPEN' && ticket.status !== 'OPEN' && ticket.status !== 'IN_PROGRESS') {
      return false;
    }
    if (statusFilter === 'RESOLVED' && ticket.status !== 'RESOLVED') {
      return false;
    }
    
    // Type filter
    if (typeFilter !== 'ALL' && ticket.category !== typeFilter) {
      return false;
    }
    
    return true;
  });

  // Count stats
  const openCount = tickets.filter(t => t.status === 'OPEN' || t.status === 'IN_PROGRESS').length;
  const resolvedCount = tickets.filter(t => t.status === 'RESOLVED').length;

  return (
    <div style={styles.container}>
      <div style={styles.splitLayout}>
        
        {/* Left: Tickets queue */}
        <div style={styles.sidebarPanel} className="glass-card">
          <div style={styles.panelTitleContainer}>
            <span style={styles.panelHeaderTitle}>File des Tickets ({filteredTickets.length})</span>
          </div>

          {/* Filters */}
          <div style={styles.filtersContainer}>
            {/* Status Filter */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Statut</label>
              <div style={styles.filterButtons}>
                <button
                  onClick={() => setStatusFilter('OPEN')}
                  style={{
                    ...styles.filterButton,
                    ...(statusFilter === 'OPEN' ? styles.filterButtonActive : {})
                  }}
                >
                  En attente ({openCount})
                </button>
                <button
                  onClick={() => setStatusFilter('RESOLVED')}
                  style={{
                    ...styles.filterButton,
                    ...(statusFilter === 'RESOLVED' ? styles.filterButtonActive : {})
                  }}
                >
                  Terminés ({resolvedCount})
                </button>
                <button
                  onClick={() => setStatusFilter('ALL')}
                  style={{
                    ...styles.filterButton,
                    ...(statusFilter === 'ALL' ? styles.filterButtonActive : {})
                  }}
                >
                  Tous ({tickets.length})
                </button>
              </div>
            </div>

            {/* Type Filter */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={styles.filterSelect}
                className="input-field"
              >
                <option value="ALL">Tous les types</option>
                <option value="DRIVER_MODIFICATION">Demandes de Modification</option>
                <option value="PAYMENT_ISSUE">Problèmes de Paiement</option>
                <option value="DRIVER_BEHAVIOR">Comportement Chauffeur</option>
                <option value="CLIENT_BEHAVIOR">Comportement Client</option>
                <option value="APP_BUG">Bugs Application</option>
                <option value="DOCUMENT_ISSUE">Problèmes de Document</option>
              </select>
            </div>
          </div>

          <div style={styles.ticketList}>
            {filteredTickets.length === 0 ? (
              <div style={styles.emptyTickets}>
                <LifeBuoy size={32} color="var(--text-muted)" />
                <p style={{ marginTop: '8px', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  Aucun ticket trouvé
                </p>
              </div>
            ) : (
              filteredTickets.map((t) => {
                const isSelected = selectedTicket?.id === t.id;
                return (
                  <div 
                    key={t.id} 
                    onClick={() => handleSelectTicket(t.id)}
                    style={{
                      ...styles.ticketListItem,
                      ...(isSelected ? styles.ticketListItemActive : {})
                    }}
                  >
                    <div style={styles.ticketMeta}>
                      <span style={styles.ticketListCategory}>{getCategoryLabel(t.category)}</span>
                      <span style={styles.ticketListReporter}>Reporté par : {t.reporterType}</span>
                    </div>
                    <span className={`status-badge ${
                      t.status === 'OPEN' ? 'warning' : t.status === 'RESOLVED' ? 'success' : 'info'
                    }`} style={{ fontSize: '0.6rem' }}>
                      {t.status}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: Active conversation window */}
        <div style={styles.chatPanel} className="glass-card">
          {selectedTicket ? (
            <>
              {/* Chat Header */}
              <div style={styles.chatHeader}>
                <div>
                  <h3 style={styles.chatTitle}>{getCategoryLabel(selectedTicket.category)}</h3>
                  <div style={styles.chatSubDetails}>
                    <span>Reporter ID : {selectedTicket.reporterId} ({selectedTicket.reporterType})</span>
                    {selectedTicket.relatedRideId && (
                      <span style={{ marginLeft: '12px' }}>Course associée : {selectedTicket.relatedRideId}</span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                    style={styles.statusSelect}
                    className="input-field"
                  >
                    <option value="OPEN">En attente (OPEN)</option>
                    <option value="IN_PROGRESS">En cours (IN_PROGRESS)</option>
                    <option value="RESOLVED">Résolu (RESOLVED)</option>
                    <option value="ESCALATED">Escaladé (ESCALATED)</option>
                  </select>

                  {selectedTicket.status !== 'RESOLVED' && selectedTicket.category === 'DRIVER_MODIFICATION' ? (
                    <>
                      <button 
                        onClick={() => {
                          setModificationAction('approve');
                          setModificationModalOpen(true);
                        }}
                        style={{...styles.refundBtn, backgroundColor: '#10b981'}}
                        className="btn-primary"
                        disabled={actionLoading}
                      >
                        <CheckCircle size={16} />
                        <span>Approuver</span>
                      </button>
                      <button 
                        onClick={() => {
                          setModificationAction('reject');
                          setModificationModalOpen(true);
                        }}
                        style={{...styles.refundBtn, backgroundColor: '#ef4444'}}
                        className="btn-primary"
                        disabled={actionLoading}
                      >
                        <X size={16} />
                        <span>Rejeter</span>
                      </button>
                    </>
                  ) : selectedTicket.status !== 'RESOLVED' && (
                    <button 
                      onClick={() => setRefundModalOpen(true)}
                      style={styles.refundBtn}
                      className="btn-primary"
                    >
                      <DollarSign size={16} />
                      <span>Geste Commercial / Rembourser</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Comparison Panel */}
              {selectedTicket.category === 'DRIVER_MODIFICATION' && modificationRequest && (
                <div style={styles.comparisonContainer}>
                  <div style={styles.comparisonHeader}>
                    <h4 style={styles.comparisonTitle}>
                      {modificationRequest.type === 'PROFILE_PHOTO' 
                        ? "Vérification d'Identité (Nouvel Avatar vs Permis/CNI)" 
                        : "Vérification du Véhicule (Nouvelle Photo/Infos vs Carte Grise)"}
                    </h4>
                  </div>
                  <div style={styles.comparisonBody}>
                    <div style={styles.comparisonColumn}>
                      <span style={styles.comparisonColLabel}>Nouvelle valeur demandée</span>
                      <div style={styles.comparisonContentBox}>
                        {modificationRequest.type === 'PROFILE_PHOTO' || modificationRequest.type === 'VEHICLE_PHOTO' ? (
                          <img 
                            src={modificationRequest.photoUrl} 
                            alt="Nouvelle demande" 
                            style={styles.comparisonImage} 
                          />
                        ) : modificationRequest.type === 'VEHICLE_INFO' ? (
                          (() => {
                            let reqDetails = {};
                            try {
                              reqDetails = JSON.parse(modificationRequest.requestData);
                            } catch (e) {
                              try {
                                reqDetails = modificationRequest.requestData;
                              } catch (e2) {}
                            }
                            return (
                              <div style={styles.infoDetails}>
                                <p style={{ margin: '4px 0' }}><strong>Marque :</strong> {reqDetails.brand || reqDetails.vehicleBrand || 'N/A'}</p>
                                <p style={{ margin: '4px 0' }}><strong>Modèle :</strong> {reqDetails.model || reqDetails.vehicleModel || 'N/A'}</p>
                                <p style={{ margin: '4px 0' }}><strong>Immatriculation :</strong> {reqDetails.plate || reqDetails.licensePlate || 'N/A'}</p>
                              </div>
                            );
                          })()
                        ) : (
                          <div style={styles.noPreview}>Aperçu non disponible pour ce type</div>
                        )}
                      </div>
                    </div>

                    <div style={styles.comparisonColumn}>
                      <span style={styles.comparisonColLabel}>Document officiel de référence</span>
                      <div style={styles.comparisonContentBox}>
                        {comparisonDocLoading ? (
                          <div style={styles.comparisonLoading}>Chargement...</div>
                        ) : comparisonDocUrl ? (
                          comparisonDocIsPdf ? (
                            <iframe 
                              src={comparisonDocUrl} 
                              style={styles.comparisonIframe} 
                              title="Document de référence" 
                            />
                          ) : (
                            <img 
                              src={comparisonDocUrl} 
                              alt="Document officiel de référence" 
                              style={styles.comparisonImage} 
                            />
                          )
                        ) : (
                          <div style={styles.noPreview}>Aucun document officiel trouvé</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Message History */}
              <div style={styles.messageArea}>
                {messagesLoading ? (
                  <div style={styles.messagesLoading}>Chargement de la conversation...</div>
                ) : (
                  <div style={styles.messageList}>
                    {messages.map((msg) => {
                      const isSystem = msg.authorType === 'SYSTEM';
                      const isAdmin = msg.authorType === 'ADMIN';
                      
                      return (
                        <div 
                          key={msg.id}
                          style={{
                            ...styles.messageRow,
                            justifyContent: isSystem ? 'center' : isAdmin ? 'flex-end' : 'flex-start'
                          }}
                        >
                          {isSystem ? (
                            <div style={styles.systemMessage}>
                              {msg.content}
                            </div>
                          ) : (
                            <div style={{
                              ...styles.bubble,
                              ...(isAdmin ? styles.bubbleAdmin : styles.bubbleUser)
                            }}>
                              <div style={styles.bubbleAuthor}>
                                <User size={10} style={{ marginRight: '4px' }} />
                                <span>{msg.authorType}</span>
                              </div>
                              <p style={styles.bubbleText}>{msg.content}</p>
                              <span style={styles.bubbleTime}>
                                {new Date(msg.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Chat Footer Input */}
              {selectedTicket.status !== 'RESOLVED' ? (
                <form onSubmit={handleSendReply} style={styles.chatFooter}>
                  <input
                    type="text"
                    placeholder="Saisissez votre réponse ici..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    style={styles.chatInput}
                    className="input-field"
                    required
                  />
                  <button type="submit" style={styles.sendBtn} className="btn-primary">
                    <Send size={16} />
                  </button>
                </form>
              ) : (
                <div style={styles.resolvedBanner}>
                  <CheckCircle size={18} color="var(--success)" />
                  <span>Cette réclamation a été clôturée et résolue. Action résolue : {selectedTicket.resolutionAction}</span>
                </div>
              )}
            </>
          ) : (
            <div style={styles.emptyChat}>
              <LifeBuoy size={36} color="var(--text-muted)" />
              <p style={{ marginTop: '12px' }}>Sélectionnez un ticket pour charger le fil de discussion support client.</p>
            </div>
          )}
        </div>

      </div>

      {/* Refund / Credit Modal */}
      {refundModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent} className="glass-card">
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Geste Commercial / Remboursement</h3>
              <button onClick={() => setRefundModalOpen(false)} style={styles.closeModalBtn}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleRefundSubmit} style={styles.modalForm}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Montant (€ - Crédit direct)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 15.00"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="input-field"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Motif du remboursement</label>
                <textarea
                  placeholder="Geste commercial suite à un retard de course, erreur de paiement..."
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  style={styles.textarea}
                  className="input-field"
                  rows={4}
                  required
                />
              </div>

              <div style={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={() => setRefundModalOpen(false)}
                  className="btn-secondary"
                  style={styles.cancelBtn}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={actionLoading}
                  className="btn-primary"
                  style={styles.submitBtn}
                >
                  Valider le crédit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modificationModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent} className="glass-card">
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {modificationAction === 'approve' ? 'Approuver la demande' : 'Rejeter la demande'}
              </h3>
              <button onClick={() => setModificationModalOpen(false)} style={styles.closeModalBtn}>
                <X size={18} />
              </button>
            </div>
            <div style={styles.modalForm}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Note {modificationAction === 'reject' ? '(obligatoire)' : '(optionnelle)'}
                </label>
                <textarea
                  placeholder={
                    modificationAction === 'reject'
                      ? 'Expliquez pourquoi la demande est rejetée...'
                      : 'Ajoutez une note (optionnel)...'
                  }
                  value={modificationNote}
                  onChange={(e) => setModificationNote(e.target.value)}
                  style={styles.textarea}
                  className="input-field"
                  rows={4}
                  required={modificationAction === 'reject'}
                />
              </div>

              <div style={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={() => setModificationModalOpen(false)}
                  className="btn-secondary"
                  style={styles.cancelBtn}
                >
                  Annuler
                </button>
                <button 
                  type="button"
                  onClick={() => handleModificationAction(modificationAction)}
                  disabled={actionLoading || (modificationAction === 'reject' && !modificationNote.trim())}
                  className="btn-primary"
                  style={{
                    ...styles.submitBtn,
                    backgroundColor: modificationAction === 'approve' ? '#10b981' : '#ef4444'
                  }}
                >
                  {actionLoading ? 'Traitement...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    animation: 'fadeIn 0.4s ease',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-title)',
    fontSize: '1.1rem',
  },
  splitLayout: {
    display: 'flex',
    gap: '20px',
    flex: 1,
    minHeight: 0,
  },
  sidebarPanel: {
    flex: 1,
    minWidth: '260px',
    maxWidth: '340px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  panelTitleContainer: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
  },
  panelHeaderTitle: {
    fontFamily: 'var(--font-title)',
    fontWeight: '600',
    fontSize: '0.95rem',
    color: 'var(--text-primary)',
  },
  filtersContainer: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  filterLabel: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filterButtons: {
    display: 'flex',
    gap: '6px',
  },
  filterButton: {
    flex: 1,
    padding: '8px 12px',
    fontSize: '0.75rem',
    fontWeight: '500',
    color: 'var(--text-secondary)',
    backgroundColor: 'transparent',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  filterButtonActive: {
    color: 'var(--primary)',
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    borderColor: 'var(--primary)',
    fontWeight: '600',
  },
  filterSelect: {
    padding: '8px 12px',
    fontSize: '0.8rem',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
  },
  emptyTickets: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  ticketList: {
    flex: 1,
    overflowY: 'auto',
  },
  ticketListItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  ticketListItemActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderLeft: '4px solid var(--primary)',
  },
  ticketMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  ticketListCategory: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  ticketListReporter: {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
  },
  chatPanel: {
    flex: 2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  chatHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid var(--border-color)',
  },
  chatTitle: {
    fontSize: '1.05rem',
    fontWeight: '600',
  },
  chatSubDetails: {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    marginTop: '2px',
    display: 'flex',
    gap: '12px',
  },
  refundBtn: {
    padding: '8px 16px',
    fontSize: '0.85rem',
  },
  messageArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  messagesLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
  },
  messageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  messageRow: {
    display: 'flex',
    width: '100%',
  },
  systemMessage: {
    backgroundColor: 'rgba(16, 185, 129, 0.08)',
    border: '1px solid rgba(16, 185, 129, 0.2)',
    padding: '10px 16px',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--success)',
    fontSize: '0.8rem',
    textAlign: 'center',
    maxWidth: '80%',
    fontWeight: '500',
  },
  bubble: {
    maxWidth: '70%',
    padding: '12px 16px',
    borderRadius: '12px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  bubbleAdmin: {
    backgroundColor: 'var(--primary)',
    color: 'white',
    borderBottomRightRadius: '2px',
  },
  bubbleUser: {
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    borderBottomLeftRadius: '2px',
  },
  bubbleAuthor: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.65rem',
    color: 'rgba(255, 255, 255, 0.6)',
    textTransform: 'uppercase',
  },
  bubbleText: {
    fontSize: '0.85rem',
    lineHeight: '1.4',
  },
  bubbleTime: {
    alignSelf: 'flex-end',
    fontSize: '0.65rem',
    color: 'rgba(255, 255, 255, 0.5)',
    marginTop: '2px',
  },
  chatFooter: {
    display: 'flex',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid var(--border-color)',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  chatInput: {
    flex: 1,
  },
  sendBtn: {
    padding: '0 18px',
  },
  resolvedBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 24px',
    borderTop: '1px solid var(--border-color)',
    backgroundColor: 'var(--success-bg)',
    color: 'var(--success)',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  emptyChat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
    fontSize: '0.85rem',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(5, 7, 12, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    backdropFilter: 'blur(4px)',
  },
  modalContent: {
    width: '100%',
    maxWidth: '480px',
    padding: '28px',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '16px',
    marginBottom: '20px',
  },
  modalTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  closeModalBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: '4px',
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
  },
  textarea: {
    fontSize: '0.9rem',
    lineHeight: '1.5',
    resize: 'none',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    borderTop: '1px solid var(--border-color)',
    paddingTop: '16px',
    marginTop: '10px',
  },
  cancelBtn: {
    padding: '8px 16px',
  },
  submitBtn: {
    padding: '8px 16px',
  },
  statusSelect: {
    padding: '8px 12px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    fontSize: '0.85rem',
    cursor: 'pointer',
    outline: 'none',
  },
  comparisonContainer: {
    padding: '16px 24px',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: 'rgba(255, 255, 255, 0.01)',
  },
  comparisonHeader: {
    marginBottom: '12px',
  },
  comparisonTitle: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'var(--primary)',
  },
  comparisonBody: {
    display: 'flex',
    gap: '24px',
  },
  comparisonColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  comparisonColLabel: {
    fontSize: '0.75rem',
    fontWeight: '500',
    color: 'var(--text-secondary)',
  },
  comparisonContentBox: {
    border: '1px dashed var(--border-color)',
    borderRadius: '8px',
    height: '700px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  comparisonImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  comparisonIframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundColor: '#fff',
  },
  comparisonLoading: {
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
  },
  noPreview: {
    fontSize: '0.8rem',
    color: 'var(--text-muted)',
  },
  infoDetails: {
    padding: '12px',
    fontSize: '0.85rem',
    color: 'var(--text-primary)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-start',
  }
};
